import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  NativeDateAdapter,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import Swal from 'sweetalert2';
import { firstValueFrom } from 'rxjs';

import { PacienteService } from '../../../core/services/pacientes.service';
import { expedienteEnBlanco } from '../../../core/constants/expendiente';
import { AuthService } from '../../../core/services/auth.service';
import { UsuarioService } from '../../../core/services/usuario.service';

import pdfMake from 'pdfmake/build/pdfmake';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { RegistroSignosVitales } from '../registro-signos-vitales/registro-signos-vitales';
import { Paciente } from '../pacientes';
import { MunicipioServices } from '../../../core/services/municipio.service';
import { BarrioServices } from '../../../core/services/barrio.service';

const FORMATO_FECHA = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export class AdaptadorFechaMX extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.trim()) {
      const partes = value.trim().split('/');

      if (partes.length === 3) {
        const dia = Number(partes[0]);
        const mes = Number(partes[1]);
        const anio = Number(partes[2]);

        const fecha = new Date(anio, mes - 1, dia);

        if (
          fecha.getFullYear() === anio &&
          fecha.getMonth() === mes - 1 &&
          fecha.getDate() === dia
        ) {
          return fecha;
        }

        return null;
      }
    }

    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const anio = date.getFullYear();

    return `${dia}/${mes}/${anio}`;
  }
}

interface DatosConsentimiento {
  nombre: string;
  edad: string;
  fechaNacimiento: string;
  expediente: string;
  telefono: string;
  direccion: string;
  fecha: string;

  institucion: {
    nombre: string;
    direccion: string;
    telefono: string;
    clave: string;
  };

  tratante: {
    id: string;
    nombre: string;
    rol: string;
    cedulas: Array<{
      id?: number;
      tipoCedula: string;
      cedula: string | number;
    }>;
  };
}

interface ItemCatalogo {
  id: string | number;
  nombre: string;
  acronimo?: string;
}

// Válido si eligió un elemento del catálogo o escribió texto no vacío.
function ubicacionRequerida(control: AbstractControl): ValidationErrors | null {
  const valor = control.value;

  if (valor && typeof valor === 'object') return null;

  return typeof valor === 'string' && valor.trim() ? null : { required: true };
}

@Component({
  selector: 'app-nuevo-paciente',
  standalone: true,
  imports: [
    MatStepperModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatAutocompleteModule,
  ],
  providers: [
    provideNativeDateAdapter(),
    {
      provide: DateAdapter,
      useClass: AdaptadorFechaMX,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'es-MX',
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: FORMATO_FECHA,
    },
  ],
  templateUrl: './nuevo-paciente.html',
  styleUrl: './nuevo-paciente.scss',
})
export class NuevoPaciente implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly municipioService = inject(MunicipioServices);
  private readonly barrioService = inject(BarrioServices);
  private readonly destroyRef = inject(DestroyRef);

  municipios: ItemCatalogo[] = [];
  municipiosFiltrados: ItemCatalogo[] = [];
  municipioActual: ItemCatalogo | null = null; // municipio del catálogo seleccionado o escrito exacto

  barriosCatalogo: ItemCatalogo[] = [];
  barriosFiltrados: ItemCatalogo[] = [];

  // Muestra el nombre cuando el valor del campo es un elemento del catálogo.
  mostrarCatalogo = (valor: string | ItemCatalogo | null): string =>
    this.esItemCatalogo(valor) ? valor.nombre : (valor ?? '');
  pasoActual = 0;

  pacienteGuardado: any = null;
  guardandoPaciente = false;

  get tieneExpedienteMedico(): boolean {
    return !!(
      this.pacienteGuardado?.numeroExpedienteMedico || this.data?.paciente?.numeroExpedienteMedico
    );
  }

  private obtenerNumeroExpediente(): string {
    return String(
      this.pacienteGuardado?.numeroExpedienteMedico ??
        this.data?.paciente?.numeroExpedienteMedico ??
        '',
    ).trim();
  }

  fechaMaxima: Date = (() => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  })();

  firstFormGroup = this._formBuilder.group({
    nombre: ['', Validators.required],
    apellidoPaterno: ['', Validators.required],
    apellidoMaterno: [''],
    fechaNacimiento: [null as Date | null, Validators.required],
    genero: ['', Validators.required],
    tipoSangre: ['', Validators.required],
  });

  secondFormGroup = this._formBuilder.group({
    municipio: ['' as string | ItemCatalogo, ubicacionRequerida],
    barrio: ['' as string | ItemCatalogo, ubicacionRequerida],
    direccion: ['', Validators.required],
    telefono: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
    email: ['', Validators.email],
    parentesco: ['', Validators.required],
    contactoEmergencia: ['', Validators.required],
    direccionEmergencia: ['', Validators.required],
    telefonoEmergencia: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
    emailEmergencia: ['', Validators.email],
  });

  // Se vuelve true solo cuando el paciente se guardó en el backend.
  private readonly pacienteRegistrado = new FormControl<boolean>(false, {
    nonNullable: true,
    validators: Validators.requiredTrue,
  });

  // Este es el stepControl del paso "Contacto".
  pasoContactoGuardado = new FormGroup({
    contacto: this.secondFormGroup,
    guardado: this.pacienteRegistrado,
  });

  constructor(
    private readonly dialogRef: MatDialogRef<NuevoPaciente>,
    private readonly pacienteServicio: PacienteService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  mostrarConsentimientos = false;

  ngOnInit(): void {
    this.mostrarConsentimientos = !!this.data?.paciente;
    this.configurarCatalogos();

    const modo = this.data?.modo;

    if (modo !== 'ver' && modo !== 'editar') {
      void this.cargarMunicipios();
      return;
    }

    const paciente = this.data?.paciente;

    if (!paciente) {
      console.error('No se recibieron los datos del paciente.');
      return;
    }

    this.firstFormGroup.patchValue({
      nombre: paciente.nombre ?? '',
      apellidoPaterno: paciente.apellido ?? '',
      apellidoMaterno: paciente.segundoApellido ?? '',
      fechaNacimiento: this.convertirFechaLocal(paciente.fechaNacimiento),
      genero: paciente.genero ?? '',
      tipoSangre: paciente.tipoSangre ?? '',
    });

    this.secondFormGroup.patchValue({
      direccion: paciente.direccion ?? '',
      telefono: paciente.telefono ?? '',
      email: paciente.correo ?? '',
      parentesco: paciente.parentescoContactoEmergencia ?? '',
      contactoEmergencia: paciente.nombreContactoEmergencia ?? '',
      direccionEmergencia: paciente.direccionContactoEmergencia ?? '',
      telefonoEmergencia: paciente.telefonoContactoEmergencia ?? '',
      emailEmergencia: paciente.correoContactoEmergencia ?? '',
    });

    // Municipio y barrio necesitan el catálogo cargado para mostrar el nombre
    void this.cargarMunicipios().then(() => this.precargarUbicacion(paciente));

    if (modo === 'ver') {
      this.firstFormGroup.disable();
      this.secondFormGroup.disable();
      this.pacienteRegistrado.setValue(true);
    }
  }

  // ==========================================================
  // CATÁLOGO DE MUNICIPIOS Y BARRIOS
  // ==========================================================

  private configurarCatalogos(): void {
    const { municipio, barrio } = this.secondFormGroup.controls;

    municipio.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valor) => {
      this.municipiosFiltrados = this.filtrar(this.municipios, valor);
      this.actualizarMunicipioActual(valor);
    });

    barrio.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valor) => {
      this.barriosFiltrados = this.filtrar(this.barriosCatalogo, valor);
    });
  }

  private async cargarMunicipios(): Promise<void> {
    try {
      const respuesta: any = await firstValueFrom(this.municipioService.obtenerMunicipio());
      this.municipios = Array.isArray(respuesta) ? respuesta : (respuesta?.data ?? []);
    } catch (error) {
      // Sin catálogo el campo sigue funcionando como texto libre
      console.error('No se pudo cargar el catálogo de municipios:', error);
      this.municipios = [];
    }

    this.municipiosFiltrados = this.filtrar(
      this.municipios,
      this.secondFormGroup.controls.municipio.value,
    );
  }

  private async cargarBarrios(municipioId: string | number): Promise<void> {
    try {
      const respuesta: any = await firstValueFrom(
        this.barrioService.obtenerBarriosPorMunicipio(municipioId),
      );

      // Si mientras tanto eligieron otro municipio, se descarta esta respuesta
      if (String(this.municipioActual?.id) !== String(municipioId)) return;

      this.barriosCatalogo = Array.isArray(respuesta) ? respuesta : (respuesta?.data ?? []);
    } catch (error) {
      console.error('No se pudo cargar el catálogo de barrios:', error);

      if (String(this.municipioActual?.id) === String(municipioId)) {
        this.barriosCatalogo = [];
      }
    }

    this.barriosFiltrados = this.filtrar(
      this.barriosCatalogo,
      this.secondFormGroup.controls.barrio.value,
    );
  }

  private actualizarMunicipioActual(valor: string | ItemCatalogo | null): void {
    const resuelto = this.resolverCatalogo(valor, this.municipios);

    if ((resuelto?.id ?? null) === (this.municipioActual?.id ?? null)) return;

    this.municipioActual = resuelto;
    this.barriosCatalogo = [];
    this.barriosFiltrados = [];

    // Un barrio elegido del catálogo anterior ya no corresponde al nuevo municipio
    const barrio = this.secondFormGroup.controls.barrio;
    if (this.esItemCatalogo(barrio.value)) {
      barrio.setValue('');
    }

    if (resuelto) {
      void this.cargarBarrios(resuelto.id);
    }
  }

  private async precargarUbicacion(paciente: any): Promise<void> {
    const municipio = this.leerUbicacion(paciente?.municipio, this.municipios);

    this.secondFormGroup.controls.municipio.setValue(municipio, { emitEvent: false });
    this.municipiosFiltrados = this.municipios;
    this.municipioActual = this.resolverCatalogo(municipio, this.municipios);

    if (this.municipioActual) {
      await this.cargarBarrios(this.municipioActual.id);
    }

    this.secondFormGroup.controls.barrio.setValue(
      this.leerUbicacion(paciente?.barrio, this.barriosCatalogo),
    );
  }

  // Convierte lo que devuelve el backend al valor que usa el campo.
  private leerUbicacion(campo: any, lista: ItemCatalogo[]): string | ItemCatalogo {
    if (!campo) return '';

    if (typeof campo === 'string') return campo;

    // Formato { tipo: 'catalogo' | 'texto', valor }
    if ('tipo' in campo) {
      if (campo.tipo === 'catalogo') {
        return lista.find((i) => String(i.id) === String(campo.valor)) ?? '';
      }
      return String(campo.valor ?? '');
    }

    // Formato { id, nombre }
    if (campo.id && campo.nombre) return campo as ItemCatalogo;

    return '';
  }

  // Arma { tipo, valor } para el backend.
  private construirCampoCatalogo(
    valor: string | ItemCatalogo | null,
    lista: ItemCatalogo[],
  ): { tipo: 'catalogo' | 'texto'; valor: string } {
    const item = this.resolverCatalogo(valor, lista);

    if (item) {
      return { tipo: 'catalogo', valor: String(item.id) };
    }

    return { tipo: 'texto', valor: typeof valor === 'string' ? valor.trim() : '' };
  }

  // Si eligió un elemento, lo devuelve; si escribió el nombre exacto de uno, también.
  private resolverCatalogo(
    valor: string | ItemCatalogo | null,
    lista: ItemCatalogo[],
  ): ItemCatalogo | null {
    if (this.esItemCatalogo(valor)) return valor;

    if (typeof valor !== 'string' || !valor.trim()) return null;

    const buscado = this.normalizar(valor);
    return lista.find((i) => this.normalizar(i.nombre) === buscado) ?? null;
  }

  private filtrar(lista: ItemCatalogo[], valor: string | ItemCatalogo | null): ItemCatalogo[] {
    // Con un elemento ya elegido se muestra la lista completa
    if (typeof valor !== 'string') return lista;

    const texto = this.normalizar(valor);
    if (!texto) return lista;

    return lista.filter((i) => this.normalizar(i.nombre).includes(texto));
  }

  private normalizar(texto: string): string {
    return texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private esItemCatalogo(valor: unknown): valor is ItemCatalogo {
    return !!valor && typeof valor === 'object';
  }

  get ayudaMunicipio(): string {
    const valor = this.secondFormGroup.controls.municipio.value;

    if (this.municipioActual) return 'Municipio del catálogo';

    return typeof valor === 'string' && valor.trim()
      ? 'Se guardará como texto libre'
      : 'Elige del catálogo o escribe uno';
  }

  get ayudaBarrio(): string {
    const valor = this.secondFormGroup.controls.barrio.value;

    if (this.resolverCatalogo(valor, this.barriosCatalogo)) return 'Barrio del catálogo';

    if (typeof valor === 'string' && valor.trim()) return 'Se guardará como texto libre';

    return this.barriosCatalogo.length
      ? 'Elige del catálogo o escribe uno'
      : 'Escribe el barrio o colonia';
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  get esUltimoPaso(): boolean {
    return this.pasoActual >= 2;
  }

  get soloLectura(): boolean {
    return this.data?.modo === 'ver';
  }

  onStepChange(index: number): void {
    this.pasoActual = index;
  }

  anterior(stepper: MatStepper): void {
    stepper.previous();
  }

  siguiente(stepper: MatStepper): void {
    if (!this.validarStepActual()) {
      return;
    }

    stepper.next();
  }

  validarStepActual(): boolean {
    if (this.soloLectura) {
      return true;
    }

    if (this.pasoActual === 0) {
      this.firstFormGroup.updateValueAndValidity();

      if (this.firstFormGroup.valid) {
        return true;
      }

      this.firstFormGroup.markAllAsTouched();

      const primerCampoInvalido = Object.keys(this.firstFormGroup.controls).find((nombre) => {
        return this.firstFormGroup.controls[nombre as keyof typeof this.firstFormGroup.controls]
          .invalid;
      });

      this.enfocarCampoInvalido(primerCampoInvalido);
      return false;
    }

    if (this.pasoActual === 1) {
      this.secondFormGroup.updateValueAndValidity();

      if (this.secondFormGroup.valid) {
        return true;
      }

      this.secondFormGroup.markAllAsTouched();

      const primerCampoInvalido = Object.keys(this.secondFormGroup.controls).find((nombre) => {
        return this.secondFormGroup.controls[nombre as keyof typeof this.secondFormGroup.controls]
          .invalid;
      });

      this.enfocarCampoInvalido(primerCampoInvalido);
      return false;
    }

    return true;
  }

  private enfocarCampoInvalido(nombre: string | undefined): void {
    if (!nombre) {
      return;
    }

    setTimeout(() => {
      const elemento = document.querySelector(
        `[formControlName="${nombre}"]`,
      ) as HTMLElement | null;

      elemento?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      elemento?.focus();
    });
  }

  private convertirFechaLocal(fecha: string | Date | null): Date | null {
    if (!fecha) {
      return null;
    }

    if (fecha instanceof Date) {
      if (isNaN(fecha.getTime())) {
        return null;
      }

      return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    }

    const fechaTexto = fecha.split('T')[0];
    const partes = fechaTexto.split('-');

    if (partes.length !== 3) {
      return null;
    }

    const [anio, mes, dia] = partes.map(Number);
    const resultado = new Date(anio, mes - 1, dia);

    if (
      resultado.getFullYear() !== anio ||
      resultado.getMonth() !== mes - 1 ||
      resultado.getDate() !== dia
    ) {
      return null;
    }

    return resultado;
  }

  private formatearFecha(fecha: Date | string | null): string | null {
    if (!fecha) {
      return null;
    }

    const fechaLocal = this.convertirFechaLocal(fecha);

    if (!fechaLocal) {
      return null;
    }

    const anio = fechaLocal.getFullYear();
    const mes = String(fechaLocal.getMonth() + 1).padStart(2, '0');
    const dia = String(fechaLocal.getDate()).padStart(2, '0');

    return `${anio}-${mes}-${dia}`;
  }

  private formatearFechaLegible(fecha: Date | string | null): string {
    const fechaISO = this.formatearFecha(fecha);

    if (!fechaISO) {
      return 'No registrada';
    }

    const [anio, mes, dia] = fechaISO.split('-');
    return `${dia}/${mes}/${anio}`;
  }

  soloNumeros(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value.replace(/\D/g, '').slice(0, 10);

    input.value = valor;

    const control = this.secondFormGroup.get(controlName);

    control?.setValue(valor);
    control?.markAsDirty();
    control?.updateValueAndValidity();
  }

  obtenerEdad(): string {
    return this.calcularEdad(this.firstFormGroup.get('fechaNacimiento')?.value ?? null);
  }

  private calcularEdad(fecha: Date | null): string {
    if (!fecha || isNaN(fecha.getTime())) {
      return 'No registrada';
    }

    const hoy = new Date();

    let anios = hoy.getFullYear() - fecha.getFullYear();
    let meses = hoy.getMonth() - fecha.getMonth();
    let dias = hoy.getDate() - fecha.getDate();

    if (dias < 0) {
      meses--;

      const ultimoDiaMesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate();

      dias += ultimoDiaMesAnterior;
    }

    if (meses < 0) {
      anios--;
      meses += 12;
    }

    if (anios < 0) {
      return 'No válida';
    }

    if (anios === 0 && meses === 0) {
      return `${Math.max(dias, 0)} días`;
    }

    if (anios === 0) {
      return `${meses} meses`;
    }

    if (anios <= 10 && meses > 0) {
      return `${anios} años y ${meses} meses`;
    }

    return `${anios} años`;
  }

  async guardarPaciente(stepper: MatStepper): Promise<void> {
    if (this.soloLectura) {
      this.cerrar();
      return;
    }

    // Evitar envíos duplicados.
    if (this.guardandoPaciente) {
      return;
    }

    this.firstFormGroup.markAllAsTouched();
    this.secondFormGroup.markAllAsTouched();

    if (this.firstFormGroup.invalid) {
      stepper.selectedIndex = 0;

      await Swal.fire({
        icon: 'warning',
        title: 'Datos incompletos',
        text: 'Revisa los datos personales del paciente.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    if (this.secondFormGroup.invalid) {
      stepper.selectedIndex = 1;

      await Swal.fire({
        icon: 'warning',
        title: 'Datos incompletos',
        text: 'Revisa los datos de contacto y del contacto de emergencia.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    const esEditar = this.data?.modo === 'editar';
    const idPaciente = this.data?.paciente?.id;

    if (esEditar && !idPaciente) {
      await Swal.fire({
        icon: 'error',
        title: 'No se puede actualizar',
        text: 'No se encontró el identificador del paciente.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    const personales = this.firstFormGroup.getRawValue();
    const contacto = this.secondFormGroup.getRawValue();

    let expediente: any;

    if (esEditar) {
      expediente = this.data?.paciente?.expediente;

      if (expediente === undefined || expediente === null || expediente === '') {
        await Swal.fire({
          icon: 'error',
          title: 'No se puede actualizar',
          text:
            'No se recibió el expediente actual del paciente. ' +
            'Obtén los datos completos antes de editarlo para evitar ' +
            'perder su historial clínico.',
          showConfirmButton: false,
          timer: 3000,
        });

        return;
      }
    } else {
      expediente = expedienteEnBlanco();
    }

    const paciente = {
      nombre: personales.nombre?.trim(),
      apellido: personales.apellidoPaterno?.trim(),
      segundoApellido: personales.apellidoMaterno?.trim() || null,
      fechaNacimiento: this.formatearFecha(personales.fechaNacimiento),
      genero: personales.genero,
      tipoSangre: personales.tipoSangre,

      telefono: contacto.telefono,
      direccion: contacto.direccion?.trim(),
      correo: contacto.email?.trim() || null,

      nombreContactoEmergencia: contacto.contactoEmergencia?.trim(),
      direccionContactoEmergencia: contacto.direccionEmergencia?.trim(),
      telefonoContactoEmergencia: contacto.telefonoEmergencia,
      correoContactoEmergencia: contacto.emailEmergencia?.trim() || null,
      parentescoContactoEmergencia: contacto.parentesco,
      municipio: this.construirCampoCatalogo(contacto.municipio, this.municipios),
      barrio: this.construirCampoCatalogo(contacto.barrio, this.barriosCatalogo),
      expediente,
    };

    const confirmacion = await Swal.fire({
      title: esEditar ? '¿Guardar los cambios?' : '¿Registrar paciente?',
      text: esEditar
        ? 'Se actualizarán los datos del paciente.'
        : 'Se registrará un nuevo paciente.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: esEditar ? 'Guardar cambios' : 'Registrar',
      confirmButtonColor: '#24575A',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (!confirmacion.isConfirmed) {
      return;
    }

    this.guardandoPaciente = true;

    Swal.fire({
      title: esEditar ? 'Actualizando paciente...' : 'Registrando paciente...',
      text: 'Por favor, espera.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    const peticion = esEditar
      ? this.pacienteServicio.actualizarPaciente(idPaciente, paciente)
      : this.pacienteServicio.crearPaciente(paciente);

    peticion.subscribe({
      next: async (respuesta: any) => {
        try {
          // console.log('Respuesta del backend al guardar paciente:', respuesta);

          const pacienteGuardado = respuesta?.data ?? respuesta;

          this.pacienteGuardado = pacienteGuardado;

          // Cerrar el indicador de carga, NO el diálogo de Angular.
          Swal.close();

          this.pacienteGuardado = respuesta?.data ?? respuesta;
          this.mostrarConsentimientos = true;

          if (!pacienteGuardado?.numeroExpedienteMedico) {
            await Swal.fire({
              icon: 'warning',
              title: 'Expediente no disponible',
              text:
                'El paciente se guardó, pero la respuesta del servidor ' +
                'no contiene numeroExpedienteMedico. Revisa la respuesta ' +
                'del backend para recuperar el número asignado.',
              showConfirmButton: false,
              timer: 2000,
            });

            return;
          }

          await Swal.fire({
            icon: 'success',
            title: esEditar ? 'Paciente actualizado' : 'Paciente registrado',
            html: `El paciente se guardó correctamente.<br><br>`,
            // `<strong>Expediente médico:</strong> ` +
            // `${pacienteGuardado.numeroExpedienteMedico}`,
            showConfirmButton: false,
            timer: 2000,
          });

          this.pacienteRegistrado.setValue(true);

          stepper.next();
          // No usamos this.dialogRef.close().
          // El diálogo permanece abierto para generar los consentimientos.
        } catch (error) {
          console.error('Error procesando la respuesta:', error);
          Swal.close();

          await Swal.fire({
            icon: 'error',
            title: 'Error al procesar la respuesta',
            text: 'El paciente pudo haberse guardado. Verifica los datos antes de volver a registrarlo.',
            showConfirmButton: false,
            timer: 2000,
          });
        } finally {
          this.guardandoPaciente = false;
        }
      },

      error: async (error: any) => {
        // console.error('Error al guardar paciente:', error);
        // console.error('Detalle del backend:', error?.error);

        // Cerrar el indicador antes de mostrar el error.
        Swal.close();

        const mensajeBackend = error?.error?.message ?? error?.error?.mensaje;

        const detalle = Array.isArray(mensajeBackend)
          ? mensajeBackend.join('\n')
          : (mensajeBackend ?? 'Ocurrió un error al guardar los datos del paciente.');

        try {
          await Swal.fire({
            icon: 'error',
            title: esEditar ? 'Error al actualizar' : 'Error al registrar',
            text: detalle,
            showConfirmButton: false,
            timer: 2000,
          });
        } finally {
          this.guardandoPaciente = false;
        }
      },
    });
  }

  rellenarFormulario(): void {
    this.firstFormGroup.patchValue({
      nombre: 'Juan',
      apellidoPaterno: 'Pérez',
      apellidoMaterno: 'López',
      fechaNacimiento: new Date(1995, 4, 15),
      genero: 'masculino',
      tipoSangre: 'O+',
    });

    this.secondFormGroup.patchValue({
      municipio: 'Huajuapan de León',
      barrio: 'Centro',
      direccion: 'Calle Principal 123',
      telefono: '9511234567',
      email: 'juan@example.com',
      parentesco: 'Madre',
      contactoEmergencia: 'María López',
      direccionEmergencia: 'Calle Principal 123',
      telefonoEmergencia: '9517654321',
      emailEmergencia: 'maria@example.com',
    });
  }

  private readonly dialog = inject(MatDialog);

  abrirRegistroSignosVitales(paciente: Paciente) {
    this.dialog.open(RegistroSignosVitales, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      data: paciente,
    });

    this.dialogRef.close({
      abrirRegistroSignosVitales: true,
      paciente,
    });
  }

  // ==========================================================
  // DATOS DE LA INSTITUCIÓN Y DEL USUARIO TRATANTEllllll
  // ==========================================================

  private async obtenerDatosInstitucionYTratante(): Promise<{
    institucion: DatosConsentimiento['institucion'];
    tratante: DatosConsentimiento['tratante'];
  }> {
    const usuarioSesion = this.authService.getUser();

    if (!usuarioSesion?.id) {
      throw new Error('No se encontró el usuario de la sesión actual.');
    }

    const respuesta: any = await firstValueFrom(
      this.usuarioService.obtenerUsuario(usuarioSesion.id),
    );

    const usuario = respuesta?.data ?? respuesta;

    if (!usuario?.id) {
      throw new Error('No se pudieron recuperar los datos del usuario tratante.');
    }

    const nombreCompleto = [usuario.nombre, usuario.apellidoPaterno, usuario.apellidoMaterno]
      .filter((parte: string | null | undefined) => typeof parte === 'string' && !!parte.trim())
      .join(' ');

    return {
      institucion: {
        nombre: usuario.clinica?.nombre ?? 'No registrada',
        direccion: usuario.clinica?.direccion ?? 'No registrada',
        telefono: usuario.clinica?.telefono ?? 'No registrado',
        clave: usuario.clinica?.claveClinica ?? 'No registrada',
      },

      tratante: {
        id: usuario.id,
        nombre: nombreCompleto || 'No registrado',
        rol: usuario.rol?.nombre ?? 'No registrado',
        cedulas: Array.isArray(usuario.cedulas) ? usuario.cedulas : [],
      },
    };
  }

  private async obtenerDatosConsentimiento(): Promise<DatosConsentimiento> {
    const datosInstitucionYTratante = await this.obtenerDatosInstitucionYTratante();

    const personales = this.firstFormGroup.getRawValue();
    const contacto = this.secondFormGroup.getRawValue();
    const fechaNacimiento = personales.fechaNacimiento ?? null;

    const nombrePaciente = [
      personales.nombre,
      personales.apellidoPaterno,
      personales.apellidoMaterno,
    ]
      .filter(Boolean)
      .join(' ');

    const expedienteOriginal = this.data?.paciente?.expediente;

    // El expediente puede venir como texto o como objeto.
    const expediente = this.obtenerNumeroExpediente();

    if (!expediente) {
      throw new Error(
        'No se puede generar el consentimiento porque el paciente ' +
          'todavía no tiene un número de expediente médico asignado.',
      );
    }
    return {
      nombre: nombrePaciente || 'No registrado',
      edad: this.calcularEdad(fechaNacimiento),
      fechaNacimiento: this.formatearFechaLegible(fechaNacimiento),
      expediente,
      telefono: contacto.telefono || 'No registrado',
      direccion: contacto.direccion || 'No registrada',
      fecha: this.formatearFechaLegible(new Date()),

      ...datosInstitucionYTratante,
    };
  }

  // ==========================================================
  // ENCABEZADO COMÚN PARA LOS DOS CONSENTIMIENTOS
  // ==========================================================

  private ConsentimientoMenores(titulo: string, datos: DatosConsentimiento): any[] {
    return [
      {
        text: datos.institucion.nombre,
        style: 'institucion',
        alignment: 'left',
      },
      {
        style: 'tableExample',
        table: {
          widths: [50, '*', '*'],
          body: [
            [
              {
                text: 'LOGO',
                rowSpan: 2,
                alignment: 'center',
                bold: true,
                color: '#24575A',
                margin: [2, 5, 2, 5],
              },
              {
                text: titulo,
                rowSpan: 2,
                style: 'tituloTabla',
                margin: [4, 5, 4, 5],
              },
              {
                text: datos.institucion.nombre,
                style: 'dato',
                bold: true,
              },
            ],
            [
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: `Nombre del paciente: ${datos.nombre}`,
                style: 'dato',
              },
            ],
            [
              {
                rowSpan: 3,
                colSpan: 2,
                text:
                  'CON FUNDAMENTO EN EL REGLAMENTO DE LA LEY GENERAL ' +
                  'DE SALUD EN MATERIA DE PRESTACIÓN DE SERVICIOS DE ' +
                  'ATENCIÓN MÉDICA. ARTÍCULOS 80, 81, 82, 83 Y A LA ' +
                  'NORMA OFICIAL MEXICANA NOM-004-SSA3-2012, DEL ' +
                  'EXPEDIENTE CLÍNICO, FRACCIONES 10.1 A LA 10.1.4.',
                style: 'legal',
                valign: 'center',
              },
              '',
              {
                text:
                  '¿Es derecho habiente?\n' +
                  'Sí (  )     No (  )\n' +
                  'Número de Seguro Social:\n' +
                  '________________________________',
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Expediente: ${datos.expediente}`,
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Edad: ${datos.edad}`,
                style: 'dato',
              },
            ],
            [
              {
                colSpan: 2,
                columns: [
                  {
                    text: 'Servicio: __________________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Cama No.: __________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                ],
              },
              {},
              {
                text: `Lugar: ${datos.institucion.direccion} \nFecha: ${datos.fecha}`,
                style: 'campo',
                margin: [5, 5, 5, 5],
              },
            ],
            [
              {
                stack: [
                  ...this.bloqueYo('Nombre(s) del paciente, familiar o tutor legal'),
                  {
                    alignment: 'justify',
                    text:
                      `Expreso mi libre voluntad para ` +
                      'autorizar el procedimiento menor o intervención ' +
                      'quirúrgica señalada en este documento, después de ' +
                      'haberme proporcionado la información completa sobre ' +
                      'mi enfermedad y estado actual, la cual fue realizada ' +
                      'en forma amplia, precisa y suficiente en un lenguaje ' +
                      'claro y sencillo, informándome sobre los posibles ' +
                      'riesgos, complicaciones y secuelas, de igual forma ' +
                      'los beneficios.\n\n' +
                      'El médico me informó la existencia de procedimientos ' +
                      'alternativos, el derecho a cambiar mi decisión en ' +
                      'cualquier momento y manifestarla antes del procedimiento ' +
                      'o intervención. Con el propósito de que mi atención sea ' +
                      'adecuada, me comprometo a proporcionar información ' +
                      'completa y veraz, así como seguir las indicaciones médicas.\n\n' +
                      'Otorgo mi autorización al personal de salud para la ' +
                      'atención de contingencias y urgencias derivadas del ' +
                      'acto médico señalado, atendiendo al principio de ' +
                      'libertad prescriptiva.',
                  },
                ],

                colSpan: 3,
                style: 'cuerpo',
              },
            ],
            [
              {
                colSpan: 3,
                columns: [
                  {
                    text: 'Procedimiento o intervención quirúrgica',
                    width: '*',
                    bold: true,
                    fontSize: 9,
                    color: '#24575A',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Electiva (  )',
                    width: 'auto',
                    alignment: 'center',
                    margin: [5, 5, 5, 5],
                    fontSize: 9,
                  },
                  {
                    text: 'Urgente (  )',
                    width: 'auto',
                    alignment: 'center',
                    margin: [5, 5, 10, 5],
                    fontSize: 9,
                  },
                ],
                columnGap: 10,
                margin: [0, 0, 0, 0],
              },
              '',
              '',
            ],
            [
              {
                text: 'Diagnóstico previo al procedimiento o intervención quirúrgica:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
            [
              {
                text: 'Procedimiento menor o intervención quirúrgica proyectada:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
            [
              {
                text:
                  'Riesgos más frecuentes inherentes al procedimiento menor ' +
                  'o intervención quirúrgica y a las condiciones actuales ' +
                  'del paciente:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
            [
              {
                text: 'Beneficios:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) =>
            i === 0 || i === node.table.body.length ? 0.7 : 0.4,
          vLineWidth: () => 0.4,
          hLineColor: () => '#9AA8A8',
          vLineColor: () => '#9AA8A8',
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
      },
    ];
  }

  private ConsentimientoInternamiento(titulo: string, datos: DatosConsentimiento): any[] {
    return [
      {
        text: datos.institucion.nombre,
        style: 'institucion',
        alignment: 'left',
      },
      {
        style: 'tableExample',
        table: {
          widths: [50, '*', '*'],
          body: [
            [
              {
                text: 'LOGO',
                rowSpan: 2,
                alignment: 'center',
                bold: true,
                color: '#24575A',
                margin: [2, 5, 2, 5],
              },
              {
                text: titulo,
                rowSpan: 2,
                style: 'tituloTabla',
                margin: [4, 5, 4, 5],
              },
              {
                text: datos.institucion.nombre,
                style: 'dato',
                bold: true,
              },
            ],
            [
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: `Nombre del paciente: ${datos.nombre}`,
                style: 'dato',
              },
            ],
            [
              {
                rowSpan: 2,
                colSpan: 2,
                text:
                  '¿Es derecho habiente?\n' +
                  'Sí (  )     No (  )\n' +
                  'Número de Seguro Social:\n' +
                  '________________________________',
                style: 'dato',
              },
              '',
              {
                text: `Expediente: ${datos.expediente}`,
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Edad: ${datos.edad}`,
                style: 'dato',
              },
            ],

            [
              {
                colSpan: 2,
                columns: [
                  {
                    text: 'Servicio: __________________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Cama No.: __________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                ],
              },
              {},
              {
                text: `Lugar: ${datos.institucion.direccion} \nFecha: ${datos.fecha}`,
                style: 'campo',
                margin: [5, 5, 5, 5],
              },
            ],
            [
              {
                colSpan: 3,
                style: 'cuerpo',

                stack: [
                  ...this.bloqueYo('Nombre(s) del paciente, familiar o tutor legal'),
                  {
                    alignment: 'justify',
                    text:
                      `Reconozco que el/la Dr. (a) ` +
                      '______________________________________________ me ha proporcionado información amplia, clara y precisa sobre el(los) motivo(s) de mi internamiento: ' +
                      '\n\n' +
                      '1.-______________________________________________________________________________________________________________________' +
                      '\n\n' +
                      '2.-______________________________________________________________________________________________________________________' +
                      '\n\n' +
                      '3.-______________________________________________________________________________________________________________________' +
                      '\n\n' +
                      'Nombre del (los) motivo(s)(diagnósticos) ' +
                      '\n\n' +
                      'Sobre el por qué y para qué está indicado el que se me interne; los beneficios esperados después de mi internamiento, así como los riesgos y las complicaciones más frecuentes que pueden presentarse propios de mi condición actual de salud:' +
                      '____________________________________________________________________________________________________________________' +
                      '\n El Doctor me informó de la existencia de otros procedimientos alternativos al que me está proponiendo y que este es el más indicado para mi caso; además me explicó, que durante o después del(os) procedimiento(s) puedo requerir la utilización de algún componente sanguíneo. Reconozco que me informó sobre los efectos y consecuencias esperados si no acepto el tratamiento o procedimiento propuesto y declaró que en todo momento existió su disposición para aclarar mis dudas o ampliar la información.\n' +
                      'Fui informado(a) que tengo el derecho de cambiar mi decisión en cualquier momento y manifestarla previo al(os) procedimiento(s). Con el fin de facilitar mi recuperación, me comprometo a acudir a revisión médica cuando se me indique; o en el caso de presentar alguna molestia o duda. \n' +
                      `Acorde a lo anterior, declaró lo siguiente: es mi decisión libre, consciente e informada aceptar mi internamiento por el(los) motivo(s) arriba mencionado(s) y los procedimientos complementarios que sean necesarios durante la misma a juicio del equipo médico, con el fin de restablecer mi salud o mejorar mi estado actual, el cual se efectuará en: ${datos.institucion.nombre}.`,
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) =>
            i === 0 || i === node.table.body.length ? 0.7 : 0.4,
          vLineWidth: () => 0.4,
          hLineColor: () => '#9AA8A8',
          vLineColor: () => '#9AA8A8',
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
      },
    ];
  }

  private ConsentimientoAmbulatorio(titulo: string, datos: DatosConsentimiento): any[] {
    return [
      {
        text: datos.institucion.nombre,
        style: 'institucion',
        alignment: 'center',
      },
      {
        style: 'tableExample',
        table: {
          widths: [50, '*', '*'],
          body: [
            [
              {
                text: 'LOGO',
                rowSpan: 2,
                alignment: 'center',
                bold: true,
                color: '#24575A',
                margin: [2, 12, 2, 12],
              },
              {
                text: titulo,
                rowSpan: 2,
                style: 'tituloTabla',
                margin: [4, 12, 4, 12],
              },
              {
                text: datos.institucion.nombre,
                style: 'dato',
                bold: true,
              },
            ],
            [
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: `Nombre del paciente: ${datos.nombre}`,
                style: 'dato',
                margin: [4, 7, 4, 7],
              },
            ],
            [
              {
                rowSpan: 3,
                colSpan: 2,
                text:
                  'CON FUNDAMENTO EN EL REGLAMENTO DE LA LEY GENERAL ' +
                  'DE SALUD EN MATERIA DE PRESTACIÓN DE SERVICIOS DE ' +
                  'ATENCIÓN MÉDICA. ARTÍCULOS 80, 81, 82, 83 Y A LA ' +
                  'NORMA OFICIAL MEXICANA NOM-004-SSA3-2012, DEL ' +
                  'EXPEDIENTE CLÍNICO, FRACCIONES 10.1 A LA 10.1.4.',
                style: 'legal',
                valign: 'center',
              },
              '',
              {
                text:
                  '¿Es derechohabiente?\n\n' +
                  'Sí (  )     No (  )\n\n' +
                  'Número de Seguro Social:\n\n' +
                  '________________________________',
                style: 'dato',
                margin: [5, 7, 5, 7],
              },
            ],
            [
              '',
              '',
              {
                text: `Expediente: ${datos.expediente}`,
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Edad: ${datos.edad}`,
                style: 'dato',
              },
            ],
            [
              {
                colSpan: 2,
                columns: [
                  {
                    text: 'Servicio: __________________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Cama No.: __________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                ],
              },
              {},
              {
                text: `Lugar: ${datos.institucion.direccion} \nFecha: ${datos.fecha}`,
                style: 'campo',
                margin: [5, 5, 5, 5],
              },
            ],
            [
              {
                colSpan: 3,
                style: 'cuerpo',

                stack: [
                  ...this.bloqueYo('Nombre(s) del paciente, familiar o tutor legal'),
                  {
                    alignment: 'justify',
                    text:
                      `Expreso mi libre voluntad para ` +
                      'autorizar llevar el tratamiento ambulatorio que el ' +
                      'médico me indique, después de haberme proporcionado ' +
                      'la información completa sobre mi enfermedad y estado ' +
                      'actual, la cual fue realizada en forma amplia, precisa ' +
                      'y suficiente en un lenguaje claro y sencillo, ' +
                      'informándome sobre los posibles riesgos y complicaciones, ' +
                      'de igual forma los beneficios.\n' +
                      'El médico me informó la existencia de procedimientos ' +
                      'alternativos, el derecho a cambiar mi decisión en ' +
                      'cualquier momento y manifestarla antes del tratamiento, ' +
                      'procedimiento o intervención.\n' +
                      'Con el propósito de que mi atención sea adecuada, me ' +
                      'comprometo a proporcionar información completa y veraz, ' +
                      'así como seguir las indicaciones médicas.\n' +
                      'Reconozco que me informó sobre los efectos y consecuencias ' +
                      'esperados si no acepto el tratamiento o procedimiento ' +
                      'propuesto y declaró que en todo momento existió su ' +
                      'disposición para aclarar mis dudas o ampliar la información.\n' +
                      'Con el fin de facilitar mi recuperación, me comprometo ' +
                      'a acudir a revisión médica cuando se me indique; o en ' +
                      'el caso de presentar alguna molestia o duda.\n' +
                      'Acorde a lo anterior, declaro lo siguiente: es mi decisión ' +
                      'libre, consciente e informada aceptar el tratamiento ' +
                      'ambulatorio por el(los) motivo(s) arriba mencionado(s) ' +
                      'y los procedimientos complementarios que sean necesarios ' +
                      'durante la misma a juicio del equipo médico, con el fin ' +
                      'de restablecer mi salud o mejorar mi estado actual, el ' +
                      `cual se efectuará en: ${datos.institucion.nombre}.\n` +
                      'Doy mi consentimiento también para la videograbación por ' +
                      `cámaras de circuito cerrado en ${datos.institucion.nombre} ` +
                      'y autorizo la captación de imágenes de mi persona, en ' +
                      'fotografía o video, durante mi atención en el interior ' +
                      'de las instalaciones, las cuales se me explicó que serán ' +
                      'meramente para usos didácticos o académicos, cuidando ' +
                      'siempre mi privacidad y anonimato.',
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) =>
            i === 0 || i === node.table.body.length ? 0.9 : 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => '#9AA8A8',
          vLineColor: () => '#9AA8A8',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
      },
    ];
  }

  private ConsentimientoInternamiento2(titulo: string, datos: DatosConsentimiento): any[] {
    return [
      {
        text: datos.institucion.nombre,
        style: 'institucion',
        alignment: 'left',
      },
      {
        style: 'tableExample',
        table: {
          widths: [50, '*', '*'],
          body: [
            [
              {
                text: 'LOGO',
                rowSpan: 2,
                alignment: 'center',
                bold: true,
                color: '#24575A',
                margin: [2, 5, 2, 5],
              },
              {
                text: titulo,
                rowSpan: 2,
                style: 'tituloTabla',
                margin: [4, 5, 4, 5],
              },
              {
                text: datos.institucion.nombre,
                style: 'dato',
                bold: true,
              },
            ],
            [
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: `Nombre del paciente: ${datos.nombre}`,
                style: 'dato',
              },
            ],
            [
              {
                rowSpan: 3,
                colSpan: 2,
                text:
                  'CON FUNDAMENTO EN EL REGLAMENTO DE LA LEY GENERAL DE SALUD ' +
                  'EN MATERIA DE PRESTACIÓN DE SERVICIOS DE ATENCIÓN MÉDICA. ' +
                  'ARTICULOS 80, 81, 82, 83 Y A L A NORMA OFICIAL MEXICANA NOM-004- ' +
                  'SSA3-2012. DEL EXPEDIENTE CLÍNICO FRACCIONES 10.1. ',
                style: 'legal',
                valign: 'center',
              },
              '',
              {
                text:
                  '¿Es derecho habiente?\n' +
                  'Sí (  )     No (  )\n' +
                  'Número de Seguro Social:\n' +
                  '________________________________',
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Expediente: ${datos.expediente}`,
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Edad: ${datos.edad}`,
                style: 'dato',
              },
            ],
            [
              {
                colSpan: 2,
                columns: [
                  {
                    text: 'Servicio: __________________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Cama No.: __________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                ],
              },
              {},
              {
                text: `Lugar: ${datos.institucion.direccion} \nFecha: ${datos.fecha}`,
                style: 'campo',
                margin: [5, 5, 5, 5],
              },
            ],
            [
              {
                colSpan: 3,
                style: 'cuerpo',
                stack: [
                  ...this.bloqueYo('Nombre(s) del paciente, Familiar o Representante Legal'),
                  {
                    alignment: 'justify',
                    text:
                      'Expreso mi libre voluntad para ' +
                      'autorizar el procedimiento menor o intervención ' +
                      'quirúrgica señalada en este documento, después de ' +
                      'haberme proporcionado la información completa sobre ' +
                      'mi enfermedad y estado actual, la cual fue realizada ' +
                      'en forma amplia, precisa y suficiente en un lenguaje ' +
                      'claro y sencillo, informándome sobre los posibles ' +
                      'riesgos, complicaciones y secuelas, de igual forma ' +
                      'los beneficios.\n\n' +
                      'El médico me informó la existencia de procedimientos ' +
                      'alternativos, el derecho a cambiar mi decisión en ' +
                      'cualquier momento y manifestarla antes del procedimiento ' +
                      'o intervención. Con el propósito de que mi atención sea ' +
                      'adecuada, me comprometo a proporcionar información ' +
                      'completa y veraz, así como seguir las indicaciones médicas.\n\n' +
                      'Otorgo mi autorización al personal de salud para la ' +
                      'atención de contingencias y urgencias derivadas del ' +
                      'acto médico señalado, atendiendo al principio de ' +
                      'libertad prescriptiva.',
                  },
                ],
              },
            ],
            [
              {
                colSpan: 3,
                columns: [
                  {
                    text: 'Internamiento',
                    width: '*',
                    bold: true,
                    fontSize: 9,
                    color: '#24575A',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Electiva (  )',
                    width: 'auto',
                    alignment: 'center',
                    margin: [5, 5, 5, 5],
                    fontSize: 9,
                  },
                  {
                    text: 'Urgente (  )',
                    width: 'auto',
                    alignment: 'center',
                    margin: [5, 5, 10, 5],
                    fontSize: 9,
                  },
                ],
                columnGap: 10,
                margin: [0, 0, 0, 0],
              },
              '',
              '',
            ],
            [
              {
                text: 'Diagnóstico (s) de ingreso:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
            [
              {
                colSpan: 3,
                columns: [
                  {
                    text: 'Plan terapéutico propuesto',
                    width: '*',
                    bold: true,
                    fontSize: 9,
                    color: '#24575A',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Médico (  )',
                    width: 'auto',
                    alignment: 'center',
                    margin: [5, 5, 5, 5],
                    fontSize: 9,
                  },
                  {
                    text: 'Quirúrgico (  )',
                    width: 'auto',
                    alignment: 'center',
                    margin: [5, 5, 10, 5],
                    fontSize: 9,
                  },
                ],
                columnGap: 10,
                margin: [0, 0, 0, 0],
              },
              '',
              '',
            ],
            [
              {
                text: 'Riesgos más frecuentes:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
            [
              {
                text: 'Beneficios:',
                colSpan: 3,
                style: 'campoEscritura',
              },
              '',
              '',
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) =>
            i === 0 || i === node.table.body.length ? 0.7 : 0.4,
          vLineWidth: () => 0.4,
          hLineColor: () => '#9AA8A8',
          vLineColor: () => '#9AA8A8',
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
      },
    ];
  }

  private ConsentimientoInternamientoDenegacion(titulo: string, datos: DatosConsentimiento): any[] {
    return [
      {
        text: datos.institucion.nombre,
        style: 'institucion',
        alignment: 'left',
      },
      {
        style: 'tableExample',
        table: {
          widths: [50, '*', '*'],
          body: [
            [
              {
                text: 'LOGO',
                rowSpan: 2,
                alignment: 'center',
                bold: true,
                color: '#24575A',
                margin: [2, 5, 2, 5],
              },
              {
                text: titulo,
                rowSpan: 2,
                style: 'tituloTabla',
                margin: [4, 5, 4, 5],
              },
              {
                text: datos.institucion.nombre,
                style: 'dato',
                bold: true,
              },
            ],
            [
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: '',
                border: [true, false, true, true],
              },
              {
                text: `Nombre del paciente: ${datos.nombre}`,
                style: 'dato',
              },
            ],
            [
              {
                rowSpan: 2,
                colSpan: 2,
                text:
                  '¿Es derecho habiente?\n' +
                  'Sí (  )     No (  )\n' +
                  'Número de Seguro Social:\n' +
                  '________________________________',
                style: 'dato',
              },
              '',
              {
                text: `Expediente: ${datos.expediente}`,
                style: 'dato',
              },
            ],
            [
              '',
              '',
              {
                text: `Edad: ${datos.edad}`,
                style: 'dato',
              },
            ],

            [
              {
                colSpan: 2,
                columns: [
                  {
                    text: 'Servicio: __________________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                  {
                    text: 'Cama No.: __________',
                    style: 'campo',
                    margin: [5, 5, 5, 5],
                  },
                ],
              },
              {},
              {
                text: `Lugar: ${datos.institucion.direccion} \nFecha: ${datos.fecha}`,
                style: 'campo',
                margin: [5, 5, 5, 5],
              },
            ],
            [
              {
                colSpan: 3,
                style: 'cuerpo',
                stack: [
                  ...this.bloqueYo('Nombre(s) del paciente, familiar o tutor legal'),
                  {
                    alignment: 'justify',
                    text:
                      '\n\nReconozco que el/la Dr. (a) ______________________________________________ me ha proporcionado información amplia, clara y precisa sobre el(los) motivo(s) de mi internamiento: ' +
                      '\n\n' +
                      '1.-______________________________________________________________________________________________________________________' +
                      '\n\n' +
                      '2.-______________________________________________________________________________________________________________________' +
                      '\n\n' +
                      '3.-______________________________________________________________________________________________________________________' +
                      '\n\n' +
                      'Nombre del (los) motivo(s)(diagnósticos) ' +
                      '\n\n' +
                      'En que consiste(n) y los objetivos del(os) mismo(o). Sobre el por qué y para qué está indicado el que se me interne; los beneficios esperados después de mi internamiento, así como los riesgos y las complicaciones más frecuentes que pueden presentarse propios de mi condición actual de salud:' +
                      '____________________________________________________________________________________________________________________' +
                      '\nReconozco que me informó sobre los efectos y consecuencias esperados si no acepto el tratamiento propuesto. Fui informado(a) que tengo el derecho de cambiar mi decisión en cualquier momento y manifestarla. He comprendido las explicaciones que se me han dado en lenguaje claro y sencillo y declaro que en todo momento existió disposición por parte del médico para aclarar dudas o ampliar información. También se me informó que puedo continuar recibiendo atención médica en el Instituto.\n' +
                      `Por lo que entiendo y declaro lo siguiente: Estoy satisfecho por la información recibida y que comprendo el alcance y los riesgos de NO ACEPTAR mi hospitalización por el(los) motivo(s) arriba mencionado(s) y lo(s) procedimiento(s) complementarios que sean necesarios durante la misma a juicio del equipo médico y en tales circunstancias NO CONSIENTO internarme en el ${datos.institucion.nombre}.`,
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) =>
            i === 0 || i === node.table.body.length ? 0.7 : 0.4,
          vLineWidth: () => 0.4,
          hLineColor: () => '#9AA8A8',
          vLineColor: () => '#9AA8A8',
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
      },
    ];
  }

  private bloqueYo(etiqueta: string, largoLinea = 45): any[] {
    return [
      {
        text: 'Yo ' + '_'.repeat(largoLinea),
        alignment: 'center',
        margin: [0, 4, 0, 0],
      },
      {
        text: etiqueta,
        alignment: 'center',
        fontSize: 8,
        color: '#37474F',
        margin: [0, 1, 0, 8],
      },
    ];
  }

  private estilosConsentimiento(): any {
    return {
      institucion: {
        fontSize: 12,
        bold: true,
        color: '#24575A',
        alignment: 'center',
        margin: [0, 0, 0, 4],
      },

      titulo: {
        fontSize: 11,
        bold: true,
        color: '#24575A',
        alignment: 'center',
        lineHeight: 1.05,
      },

      subtitulo: {
        fontSize: 10,
        bold: true,
        color: '#24575A',
        margin: [0, 5, 0, 3],
        alignment: 'center',
      },

      cuerpo: {
        fontSize: 9.3,
        color: '#263238',
        alignment: 'justify',
        lineHeight: 1.05,
        margin: [3, 4, 3, 4],
      },

      dato: {
        fontSize: 9,
        color: '#263238',
        lineHeight: 1.05,
        margin: [2, 2, 2, 2],
      },

      tituloTabla: {
        fontSize: 10.5,
        bold: true,
        color: '#24575A',
        alignment: 'center',
        lineHeight: 1.05,
        margin: [2, 2, 2, 2],
      },

      campo: {
        fontSize: 9,
        color: '#263238',
        lineHeight: 1.05,
        margin: [2, 2, 2, 2],
      },

      // Espacio reducido para que los campos quepan en una página.
      campoEscritura: {
        fontSize: 9,
        color: '#263238',
        lineHeight: 1.05,
        margin: [3, 2, 3, 18],
      },

      legal: {
        fontSize: 9,
        color: '#37474F',
        alignment: 'justify',
        lineHeight: 1.05,
        margin: [3, 3, 3, 3],
      },

      firma: {
        fontSize: 9,
        color: '#263238',
        alignment: 'center',
        lineHeight: 1.05,
      },
    };
  }

  private generarBloqueFirmas(
    datos: DatosConsentimiento,
    textoPaciente = 'Nombre y firma del paciente',
    textoTratante = `Nombre, cédula y firma del médico tratante `,
  ): any {
    // const cedulas = datos.tratante.cedulas.length
    //   ? datos.tratante.cedulas.map((cedula) => `${cedula.tipoCedula}: ${cedula.cedula}`).join('\n')
    //   : 'Cédula no registrada';

    return {
      unbreakable: true,
      margin: [0, 5, 0, 0],
      columns: [
        {
          width: '*',
          stack: [
            {
              text: '\n\n_______________________________',
              style: 'firma',
            },
            {
              text: textoPaciente,
              style: 'firma',
            },
            {
              text: datos.nombre,
              style: 'firma',
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          width: '*',
          stack: [
            {
              text: '\n\n_______________________________',
              style: 'firma',
            },
            {
              text: textoTratante,
              style: 'firma',
            },
            {
              text: '',
              style: 'firma',
              margin: [0, 3, 0, 0],
            },
            {
              text: '',
              style: 'firma',
              margin: [0, 3, 0, 0],
            },
          ],
        },
      ],
      columnGap: 10,
    };
  }

  private pieConsentimiento(): any {
    return (pagina: number, total: number) => ({
      text: `Documento de consentimiento informado | Página ${pagina} de ${total}`,
      alignment: 'center',
      fontSize: 7,
      margin: [0, 15, 0, 0],
      color: '#666666',
    });
  }

  private async mostrarErrorConsentimiento(error: unknown): Promise<void> {
    console.error('Error al generar el consentimiento informado:', error);

    await Swal.fire({
      icon: 'error',
      title: 'No se pudo generar el consentimiento',
      text:
        error instanceof Error
          ? error.message
          : 'Ocurrió un error al recuperar los datos de la institución o del usuario.',
      showConfirmButton: false,
      timer: 2000,
    });
  }

  // ==========================================================
  // CONSENTIMIENTO INFORMADO
  // ==========================================================

  async generarConsentimientoAmbulatorio(): Promise<void> {
    try {
      const datos = await this.obtenerDatosConsentimiento();

      const documento: TDocumentDefinitions = {
        pageSize: 'LETTER',
        pageMargins: [25, 20, 25, 25],

        content: [
          ...this.ConsentimientoAmbulatorio(
            'CONSENTIMIENTO INFORMADO PARA TRATAMIENTO AMBULATORIO',
            datos,
          ),

          this.generarBloqueFirmas(
            datos,
            'Paciente o representante legal',
            `Nombre, cédula y firma del médico tratante`,
          ),

          {
            text: 'TESTIGOS',
            style: 'subtitulo',
          },

          {
            unbreakable: true,
            columns: [
              {
                width: '*',
                stack: [
                  {
                    text: '\n________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 1',
                    style: 'firma',
                  },
                ],
              },
              {
                width: '*',
                stack: [
                  {
                    text: '\n________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 2',
                    style: 'firma',
                  },
                ],
              },
            ],
            columnGap: 12,
            margin: [0, 3, 0, 0],
          },
        ],

        styles: this.estilosConsentimiento(),

        defaultStyle: {
          font: 'Roboto',
          fontSize: 7.5,
          color: '#263238',
        },

        footer: this.pieConsentimiento(),
      };

      pdfMake.createPdf(documento).open();
    } catch (error) {
      await this.mostrarErrorConsentimiento(error);
    }
  }

  async generarConsentimientoProcedimientosMenores(): Promise<void> {
    try {
      const datos = await this.obtenerDatosConsentimiento();

      const documento: TDocumentDefinitions = {
        pageSize: 'LETTER',
        pageMargins: [42, 38, 42, 42],

        content: [
          ...this.ConsentimientoMenores(
            'CONSENTIMIENTO INFORMADO PARA PROCEDIMIENTOS MENORES',
            datos,
          ),

          this.generarBloqueFirmas(
            datos,
            'Paciente o representante legal',
            `Nombre, cédula y firma del médico tratante`,
          ),

          {
            text: '\nTESTIGOS',
            style: 'subtitulo',
          },
          {
            unbreakable: true,
            columns: [
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 1',
                    style: 'firma',
                  },
                ],
              },
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 2',
                    style: 'firma',
                  },
                ],
              },
            ],
            columnGap: 12,
            margin: [0, 10, 0, 0],
          },
        ],

        styles: this.estilosConsentimiento(),
        defaultStyle: {
          font: 'Roboto',
        },
        footer: this.pieConsentimiento(),
      };

      pdfMake.createPdf(documento).open();
    } catch (error) {
      await this.mostrarErrorConsentimiento(error);
    }
  }

  async generarConsentimientoInternamiento(): Promise<void> {
    try {
      const datos = await this.obtenerDatosConsentimiento();

      const documento: TDocumentDefinitions = {
        pageSize: 'LETTER',
        pageMargins: [42, 38, 42, 42],

        content: [
          ...this.ConsentimientoInternamiento('CONSENTIMIENTO INFORMADO PARA INTERNAMIENTO', datos),

          this.generarBloqueFirmas(
            datos,
            'Paciente o representante legal',
            `Nombre, cédula y firma del médico tratante`,
          ),

          {
            text: '\nTESTIGOS',
            style: 'subtitulo',
          },
          {
            unbreakable: true,
            columns: [
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 1',
                    style: 'firma',
                  },
                ],
              },
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 2',
                    style: 'firma',
                  },
                ],
              },
            ],
            columnGap: 12,
            margin: [0, 10, 0, 0],
          },
        ],

        styles: this.estilosConsentimiento(),
        defaultStyle: {
          font: 'Roboto',
        },
        footer: this.pieConsentimiento(),
      };

      pdfMake.createPdf(documento).open();
    } catch (error) {
      await this.mostrarErrorConsentimiento(error);
    }
  }

  async generarConsentimientoInternamiento2(): Promise<void> {
    try {
      const datos = await this.obtenerDatosConsentimiento();

      const documento: TDocumentDefinitions = {
        pageSize: 'LETTER',
        pageMargins: [42, 38, 42, 42],

        content: [
          ...this.ConsentimientoInternamiento2(
            'CONSENTIMIENTO INFORMADO PARA INTERNAMIENTO',
            datos,
          ),

          this.generarBloqueFirmas(
            datos,
            'Paciente o representante legal',
            `Nombre, cédula y firma del médico tratante`,
          ),

          {
            text: '\nTESTIGOS',
            style: 'subtitulo',
          },
          {
            unbreakable: true,
            columns: [
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 1',
                    style: 'firma',
                  },
                ],
              },
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 2',
                    style: 'firma',
                  },
                ],
              },
            ],
            columnGap: 12,
            margin: [0, 10, 0, 0],
          },
        ],

        styles: this.estilosConsentimiento(),
        defaultStyle: {
          font: 'Roboto',
        },
        footer: this.pieConsentimiento(),
      };

      pdfMake.createPdf(documento).open();
    } catch (error) {
      await this.mostrarErrorConsentimiento(error);
    }
  }

  async generarConsentimientoInternamientoDenegacion(): Promise<void> {
    try {
      const datos = await this.obtenerDatosConsentimiento();

      const documento: TDocumentDefinitions = {
        pageSize: 'LETTER',
        pageMargins: [42, 38, 42, 42],

        content: [
          ...this.ConsentimientoInternamientoDenegacion(
            'DENEGACIÓN DE CONSENTIMIENTO INFORMADO PARA INTERNAMIENTO',
            datos,
          ),

          this.generarBloqueFirmas(
            datos,
            'Paciente o representante legal',
            `Nombre, cédula y firma del médico tratante`,
          ),

          {
            text: '\nTESTIGOS',
            style: 'subtitulo',
          },
          {
            unbreakable: true,
            columns: [
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 1',
                    style: 'firma',
                  },
                ],
              },
              {
                width: '*',
                stack: [
                  {
                    text: '\n\n_______________________________',
                    style: 'firma',
                  },
                  {
                    text: 'Nombre y firma del testigo 2',
                    style: 'firma',
                  },
                ],
              },
            ],
            columnGap: 12,
            margin: [0, 10, 0, 0],
          },
        ],

        styles: this.estilosConsentimiento(),
        defaultStyle: {
          font: 'Roboto',
        },
        footer: this.pieConsentimiento(),
      };

      pdfMake.createPdf(documento).open();
    } catch (error) {
      await this.mostrarErrorConsentimiento(error);
    }
  }
}
